import React, { useState } from 'react'
import { Settings, Volume2, VolumeX, Bell, BellOff, Mail, MessageSquare, Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAlerts } from '@/contexts/AlertContext'

export const AlertConfigPanel: React.FC = () => {
  const { config, updateConfig, playAlertSound } = useAlerts()
  const [testNotification, setTestNotification] = useState(false)

  const handleConfigChange = (key: string, value: any) => {
    updateConfig({ [key]: value })
  }

  const handleChannelChange = (channel: string, enabled: boolean) => {
    updateConfig({
      channels: {
        ...config.channels,
        [channel]: enabled
      }
    })
  }

  const handleSoundChange = (severity: string, sound: string) => {
    updateConfig({
      sounds: {
        ...config.sounds,
        [severity]: sound
      }
    })
  }

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      console.log('Notification permission:', permission)
    }
  }

  const sendTestNotification = () => {
    if (Notification.permission === 'granted') {
      new Notification('Test Alert', {
        body: 'This is a test notification from the fraud detection system',
        icon: '/favicon.ico'
      })
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="h-6 w-6 text-foreground" />
        <h2 className="text-2xl font-bold text-foreground">Alert Configuration</h2>
      </div>

      <Tabs defaultValue="notifications" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="rules">Alert Rules</TabsTrigger>
          <TabsTrigger value="escalation">Escalation</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Volume2 className="h-5 w-5" />
                Sound Settings
              </CardTitle>
              <CardDescription>
                Configure alert sounds and volume levels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Enable Alert Sounds</Label>
                  <p className="text-xs text-muted-foreground">
                    Play audio notifications for alerts
                  </p>
                </div>
                <Switch
                  checked={config.soundEnabled}
                  onCheckedChange={(checked) => handleConfigChange('soundEnabled', checked)}
                />
              </div>

              {config.soundEnabled && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Volume: {Math.round(config.volume * 100)}%
                    </Label>
                    <Slider
                      value={[config.volume * 100]}
                      onValueChange={([value]) => handleConfigChange('volume', value / 100)}
                      max={100}
                      step={10}
                      className="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(config.sounds).map(([severity, sound]) => (
                      <div key={severity} className="space-y-2">
                        <Label className="text-sm font-medium capitalize">
                          {severity} Alert Sound
                        </Label>
                        <div className="flex gap-2">
                          <Select
                            value={sound}
                            onValueChange={(value) => handleSoundChange(severity, value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="alarm">Alarm</SelectItem>
                              <SelectItem value="warning">Warning</SelectItem>
                              <SelectItem value="notification">Notification</SelectItem>
                              <SelectItem value="chime">Chime</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => playAlertSound(severity as any)}
                          >
                            Test
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Browser Notifications
              </CardTitle>
              <CardDescription>
                Configure desktop notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Enable Browser Notifications</Label>
                  <p className="text-xs text-muted-foreground">
                    Show desktop notifications for alerts
                  </p>
                </div>
                <Switch
                  checked={config.notificationsEnabled}
                  onCheckedChange={(checked) => handleConfigChange('notificationsEnabled', checked)}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={requestNotificationPermission}
                  disabled={Notification?.permission === 'granted'}
                >
                  {Notification?.permission === 'granted' ? 'Permission Granted' : 'Request Permission'}
                </Button>
                <Button
                  variant="outline"
                  onClick={sendTestNotification}
                  disabled={Notification?.permission !== 'granted'}
                >
                  Send Test Notification
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="channels" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Channels</CardTitle>
              <CardDescription>
                Configure which notification channels to use for different alert types
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                { key: 'banner', label: 'Dashboard Banner', icon: Bell, description: 'Show alerts in the top banner' },
                { key: 'toast', label: 'Toast Notifications', icon: Bell, description: 'Show popup notifications' },
                { key: 'browser', label: 'Browser Notifications', icon: Smartphone, description: 'Send desktop notifications' },
                { key: 'email', label: 'Email Alerts', icon: Mail, description: 'Send email notifications' },
                { key: 'slack', label: 'Slack Integration', icon: MessageSquare, description: 'Send to Slack channel' }
              ].map(({ key, label, icon: Icon, description }) => (
                <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <Label className="text-sm font-medium">{label}</Label>
                      <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.channels[key as keyof typeof config.channels]}
                    onCheckedChange={(checked) => handleChannelChange(key, checked)}
                  />
                </div>
              ))}

              {config.channels.email && (
                <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                  <Label className="text-sm font-medium">Email Recipients</Label>
                  <Input
                    placeholder="team@company.com, manager@company.com"
                    defaultValue="team@company.com"
                  />
                </div>
              )}

              {config.channels.slack && (
                <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                  <Label className="text-sm font-medium">Slack Webhook URL</Label>
                  <Input
                    type="password"
                    placeholder="https://hooks.slack.com/services/..."
                  />
                  <Label className="text-sm font-medium">Channel</Label>
                  <Input
                    placeholder="#fraud-alerts"
                    defaultValue="#fraud-alerts"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Alert Trigger Rules</CardTitle>
              <CardDescription>
                Configure when alerts should be triggered based on fraud patterns
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                {
                  name: 'Sequential Phone Detection',
                  description: 'Alert when sequential phone numbers are detected',
                  threshold: 3,
                  timeWindow: '1 hour',
                  severity: 'critical'
                },
                {
                  name: 'Velocity Attack',
                  description: 'Alert when high submission rate detected',
                  threshold: 5,
                  timeWindow: '2 minutes',
                  severity: 'critical'
                },
                {
                  name: 'Zip Code Concentration',
                  description: 'Alert when single zip exceeds percentage threshold',
                  threshold: 30,
                  timeWindow: '24 hours',
                  severity: 'high'
                },
                {
                  name: 'Data Center IP Detection',
                  description: 'Alert when data center IPs are detected',
                  threshold: 1,
                  timeWindow: 'immediate',
                  severity: 'medium'
                }
              ].map((rule, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">{rule.name}</h4>
                      <p className="text-sm text-muted-foreground">{rule.description}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Threshold</Label>
                      <Input
                        type="number"
                        defaultValue={rule.threshold}
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Time Window</Label>
                      <Select defaultValue={rule.timeWindow}>
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Immediate</SelectItem>
                          <SelectItem value="2 minutes">2 minutes</SelectItem>
                          <SelectItem value="1 hour">1 hour</SelectItem>
                          <SelectItem value="24 hours">24 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Severity</Label>
                      <Select defaultValue={rule.severity}>
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="escalation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Escalation Rules</CardTitle>
              <CardDescription>
                Configure automatic escalation when alerts are not acknowledged
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                {
                  condition: 'Critical alert not acknowledged within 5 minutes',
                  action: 'Send SMS to on-call manager',
                  enabled: true
                },
                {
                  condition: 'Same pattern triggers 3+ times in 1 hour',
                  action: 'Auto-enable strict blocking mode',
                  enabled: true
                },
                {
                  condition: 'Fraud rate exceeds 50% for any hour',
                  action: 'Send emergency email to all stakeholders',
                  enabled: false
                }
              ].map((rule, index) => (
                <div key={index} className="flex items-start justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground mb-1">
                      {rule.condition}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      → {rule.action}
                    </p>
                  </div>
                  <Switch defaultChecked={rule.enabled} />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}